/**
 * Role 8 — Statement of Financial Position — calculated totals.
 *
 * Extracted verbatim from the former single-class `CalculationService`, which held
 * all 13 sheets' total calculations in one 5,557-line file. Each function is pure and
 * self-contained (no shared state, no `this`), so the split is a move, not a rewrite.
 * `CalculationService` remains the injectable entry point and delegates here.
 */

/**
 * Calculate all Role8 (210000 - Statement of Financial Position) totals based on form data
 *
 * @param formData - The form data object with control values (format: qname[member][period])
 * @param xbrlPrefix - The XBRL prefix ('ifrs-full' for MFRS, 'ifrs-smes' for MPERS)
 * @returns Object containing only the calculated total fields
 */
export function calculateTotals_210000(
  formData: Record<string, any>,
  xbrlPrefix: 'ifrs-full' | 'ifrs-smes',
): Record<string, any> {
  const calculatedTotals: Record<string, any> = {};

  // Helper function to get control name in XBRL format
  const getXbrlControlName = (
    qname: string,
    consolidatedOrSeparate: 'Consolidated' | 'Separate',
    period: 'Current' | 'Previous',
  ): string => {
    const periodLabel = period === 'Current' ? 'CurrentYear' : 'PreviousYear';
    return `${qname}[${consolidatedOrSeparate}][${periodLabel}]`;
  };

  // Process all combinations of Consolidated/Separate and Current/Previous
  ['Consolidated', 'Separate'].forEach((consolidatedOrSeparate) => {
    ['Current', 'Previous'].forEach((period) => {
      const getValue = (qname: string) => {
        const controlName = getXbrlControlName(
          qname,
          consolidatedOrSeparate as any,
          period as any,
        );
        const value = formData[controlName];
        // Explicitly convert to number to avoid string concatenation
        return typeof value === 'number' ? value : parseFloat(value) || 0;
      };

      const setValue = (qname: string, value: number) => {
        const controlName = getXbrlControlName(
          qname,
          consolidatedOrSeparate as any,
          period as any,
        );
        calculatedTotals[controlName] = value;
      };

      // Get prefix for qnames
      const prefix = xbrlPrefix;
      const customPrefix =
        prefix === 'ifrs-full' ? 'ssmt-mfrs' : 'ssmt-mpers';
      const specialPrefix = 'ssmt';

      /*
        Assets - MFRS and MPERS Calculations
      */
      // 1. Total Non-Current Assets
      const totalNonCurrentAssets =
        prefix === 'ifrs-smes'
          ? getValue(`${prefix}:PropertyPlantAndEquipment`) +
            getValue(`${prefix}:InvestmentProperty`) +
            getValue(`${customPrefix}:NoncurrentBiologicalAssets`) +
            getValue(`${prefix}:IntangibleAssetsAndGoodwill`) +
            // Intentional typo "Subisidiaries" — must match the SSM MPERS taxonomy element exactly
            // (MPERS-only; reverted in c933312). Do NOT "correct" the spelling.
            // See docs/specifications/sheets/09b_sub-classification-assets-liabilities-equity-mpers/04_investments-subsidiaries.md
            getValue(`${customPrefix}:InvestmentInSubisidiaries`) +
            getValue(`${prefix}:InvestmentsInAssociates`) +
            getValue(`${prefix}:InvestmentsInJointVentures`) +
            getValue(
              `${customPrefix}:NoncurrentInvestmentsOtherThanInvestmentsAccountedForUsingEquityMethod`,
            ) +
            getValue(`${prefix}:NoncurrentReceivables`) +
            getValue(`${prefix}:DeferredTaxAssets`) +
            getValue(`${customPrefix}:OtherNoncurrentAssets`)
          : getValue(`${prefix}:PropertyPlantAndEquipment`) +
            getValue(`${prefix}:InvestmentProperty`) +
            getValue(`${customPrefix}:NoncurrentBiologicalAssets`) +
            getValue(`${prefix}:RightofuseAssets`) +
            getValue(`${customPrefix}:NoncurrentServiceConcessionAssets`) +
            getValue(`${prefix}:IntangibleAssetsAndGoodwill`) +
            getValue(`${prefix}:InvestmentsInSubsidiaries`) +
            getValue(`${prefix}:InvestmentsInAssociates`) +
            getValue(`${prefix}:InvestmentsInJointVentures`) +
            getValue(
              `${prefix}:NoncurrentInvestmentsOtherThanInvestmentsAccountedForUsingEquityMethod`,
            ) +
            getValue(`${prefix}:NoncurrentReceivables`) +
            getValue(`${prefix}:NoncurrentContractAssets`) +
            getValue(`${prefix}:DeferredTaxAssets`) +
            getValue(`${prefix}:NoncurrentDerivativeFinancialAssets`) +
            getValue(`${prefix}:OtherNoncurrentAssets`);
      setValue(`${prefix}:NoncurrentAssets`, totalNonCurrentAssets);

      // 2. Total Current Assets other than assets held for sale (MFRS only)
      const totalCurrentAssetsOtherTHanAssetsHeldForSale =
        getValue(`${prefix}:InventoriesTotal`) +
        getValue(`${customPrefix}:CurrentBiologicalAssets`) +
        getValue(`${prefix}:CurrentInvestments`) +
        getValue(`${prefix}:CurrentTaxAssetsCurrent`) +
        getValue(`${prefix}:TradeAndOtherCurrentReceivables`) +
        getValue(`${prefix}:CurrentContractAssets`) +
        getValue(`${prefix}:CurrentDerivativeFinancialAssets`) +
        getValue(`${specialPrefix}:CashAndBankBalances`) +
        getValue(`${prefix}:OtherCurrentAssets`);
      setValue(
        `${prefix}:CurrentAssetsOtherThanAssetsOrDisposalGroupsClassifiedAsHeldForSaleOrAsHeldForDistributionToOwners`,
        totalCurrentAssetsOtherTHanAssetsHeldForSale,
      );

      // 3. Total Current Assets
      const totalCurrentAssets =
        prefix === 'ifrs-smes'
          ? getValue(`${prefix}:InventoriesTotal`) +
            getValue(`${customPrefix}:CurrentBiologicalAssets`) +
            getValue(`${customPrefix}:CurrentInvestments`) +
            getValue(`${prefix}:TradeAndOtherCurrentReceivables`) +
            getValue(`${prefix}:CurrentTaxAssetsCurrent`) +
            getValue(`${customPrefix}:CurrentDerivativeFinancialAssets`) +
            getValue(`${specialPrefix}:CashAndBankBalances`) +
            getValue(`${customPrefix}:OtherCurrentAssets`)
          : totalCurrentAssetsOtherTHanAssetsHeldForSale +
            getValue(
              `${prefix}:NoncurrentAssetsOrDisposalGroupsClassifiedAsHeldForSaleOrAsHeldForDistributionToOwners`,
            );
      setValue(`${prefix}:CurrentAssets`, totalCurrentAssets);

      // 4. Total Assets
      const totalAssets = totalNonCurrentAssets + totalCurrentAssets;
      setValue(`${prefix}:Assets`, totalAssets);

      /*
        Equity and liabilities - MFRS and MPERS Calculations
      */
      // 1. Total Equity attributable to owners
      const totalEquityAttributableToOwners =
        getValue(`${prefix}:IssuedCapital`) +
        getValue(`${prefix}:RetainedEarnings`) -
        getValue(`${prefix}:TreasuryShares`) +
        getValue(`${prefix}:OtherReserves`);
      setValue(
        `${prefix}:EquityAttributableToOwnersOfParent`,
        totalEquityAttributableToOwners,
      );

      // 2. Total Equity
      const totalEquity =
        totalEquityAttributableToOwners +
        getValue(`${customPrefix}:OtherComponentsOfEquity`) +
        getValue(`${prefix}:NoncontrollingInterests`);
      setValue(`${prefix}:Equity`, totalEquity);

      // 3. Total Non-Current Liabilities
      const totalNonCurrentLiabilities =
        prefix === 'ifrs-smes'
          ? getValue(`${customPrefix}:NoncurrentBorrowings`) +
            getValue(`${prefix}:NoncurrentProvisionsForEmployeeBenefits`) +
            getValue(`${prefix}:NoncurrentProvisions`) +
            getValue(`${prefix}:DeferredTaxLiabilities`) +
            getValue(`${prefix}:NoncurrentPayables`) +
            getValue(`${customPrefix}:OtherNoncurrentLiabilities`)
          : getValue(
              `${customPrefix}:NoncurrentServiceConcessionLiabilities`,
            ) +
            getValue(`${prefix}:LongtermBorrowings`) +
            getValue(`${customPrefix}:NoncurrentFinanceLeaseLiabilities`) +
            getValue(`${prefix}:NoncurrentProvisionsForEmployeeBenefits`) +
            getValue(`${prefix}:NoncurrentProvisions`) +
            getValue(`${prefix}:NoncurrentPayables`) +
            getValue(`${prefix}:NoncurrentContractLiabilities`) +
            getValue(`${prefix}:DeferredTaxLiabilities`) +
            getValue(`${prefix}:NoncurrentDerivativeFinancialLiabilities`) +
            getValue(`${prefix}:OtherNoncurrentLiabilities`);
      setValue(`${prefix}:NoncurrentLiabilities`, totalNonCurrentLiabilities);

      // 4. Total Current Liabilities Other Than Liabilities Held For Sale (MFRS only)
      const totalCurrentLiabilitiesOtherThanLiabilitiesHeldForSale =
        prefix === 'ifrs-full'
          ? getValue(`${prefix}:CurrentPortionOfLongtermBorrowings`) +
            getValue(`${customPrefix}:CurrentFinanceLeaseLiabilities`) +
            getValue(`${prefix}:CurrentProvisionsForEmployeeBenefits`) +
            getValue(`${prefix}:CurrentProvisions`) +
            getValue(`${prefix}:CurrentTaxLiabilitiesCurrent`) +
            getValue(`${prefix}:TradeAndOtherCurrentPayables`) +
            getValue(`${prefix}:CurrentContractLiabilities`) +
            getValue(`${prefix}:CurrentDerivativeFinancialLiabilities`) +
            getValue(`${prefix}:OtherCurrentLiabilities`)
          : 0;
      setValue(
        prefix === 'ifrs-full'
          ? `${prefix}:CurrentLiabilitiesOtherThanLiabilitiesIncludedInDisposalGroupsClassifiedAsHeldForSale`
          : '',
        totalCurrentLiabilitiesOtherThanLiabilitiesHeldForSale,
      );

      // 5. Total Current Liabilities
      const totalCurrentLiabilities =
        prefix === 'ifrs-smes'
          ? getValue(`${prefix}:ShorttermBorrowings`) +
            getValue(`${prefix}:CurrentProvisionsForEmployeeBenefits`) +
            getValue(`${prefix}:CurrentProvisions`) +
            getValue(`${prefix}:TradeAndOtherCurrentPayables`) +
            getValue(`${prefix}:CurrentTaxLiabilitiesCurrent`) +
            getValue(
              `${customPrefix}:CurrentDerivativeFinancialLiabilities`,
            ) +
            getValue(`${customPrefix}:OtherCurrentLiabilities`)
          : totalCurrentLiabilitiesOtherThanLiabilitiesHeldForSale +
            getValue(
              `${prefix}:LiabilitiesIncludedInDisposalGroupsClassifiedAsHeldForSale`,
            );
      setValue(`${prefix}:CurrentLiabilities`, totalCurrentLiabilities);

      // 6. Total Liabilities
      const totalLiabilities =
        totalNonCurrentLiabilities + totalCurrentLiabilities;
      setValue(`${prefix}:Liabilities`, totalLiabilities);

      // 7. Total Equity and Liabilities
      const totalEquityAndLiabilities = totalEquity + totalLiabilities;
      setValue(`${prefix}:EquityAndLiabilities`, totalEquityAndLiabilities);
    });
  });

  return calculatedTotals;
}
